<template>
  <div class="reporte-produccion-contador">
    <!-- Header -->
    <div class="bg-white rounded-lg shadow-md p-6 mb-6">
      <div class="flex justify-between items-center mb-4">
        <h1 class="text-3xl font-bold text-gray-800">
          📊 Reporte de Producción
        </h1>
        <button
          @click="exportarExcel"
          class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          :disabled="cargando"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Exportar Excel
        </button>
      </div>

      <!-- Filtros de fecha -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Fecha Inicio
          </label>
          <input
            type="date"
            v-model="filtros.fechaInicio"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Fecha Fin
          </label>
          <input
            type="date"
            v-model="filtros.fechaFin"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div class="flex items-end">
          <button
            @click="cargarDatos"
            class="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            :disabled="cargando"
          >
            <span v-if="!cargando">🔍 Consultar</span>
            <span v-else>⏳ Cargando...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="cargando" class="flex justify-center items-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Cargando datos de producción...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
      <div class="flex items-center gap-3">
        <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <h3 class="text-red-800 font-semibold">Error al cargar datos</h3>
          <p class="text-red-600">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div v-else-if="datosOriginales.length > 0">
      <!-- Resumen General -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md p-6 text-white">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-blue-100 text-sm font-medium">Total Pedidos</p>
              <p class="text-3xl font-bold mt-2">{{ resumenGeneral.totalPedidos }}</p>
            </div>
            <div class="bg-white bg-opacity-20 rounded-full p-3">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md p-6 text-white">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-green-100 text-sm font-medium">Total Productos</p>
              <p class="text-3xl font-bold mt-2">{{ resumenGeneral.totalProductos }}</p>
            </div>
            <div class="bg-white bg-opacity-20 rounded-full p-3">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md p-6 text-white">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-purple-100 text-sm font-medium">Vendedores</p>
              <p class="text-3xl font-bold mt-2">{{ resumenGeneral.totalVendedores }}</p>
            </div>
            <div class="bg-white bg-opacity-20 rounded-full p-3">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md p-6 text-white">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-orange-100 text-sm font-medium">Promedio Días</p>
              <p class="text-3xl font-bold mt-2">{{ resumenGeneral.promedioDiasProduccion }}</p>
            </div>
            <div class="bg-white bg-opacity-20 rounded-full p-3">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="bg-white rounded-lg shadow-md mb-6">
        <div class="border-b border-gray-200">
          <nav class="flex -mb-px">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="tabActivo = tab.id"
              :class="[
                'py-4 px-6 text-sm font-medium border-b-2 transition-colors',
                tabActivo === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.nombre }}
            </button>
          </nav>
        </div>

        <div class="p-6">
          <!-- Tab: Por Vendedor -->
          <div v-if="tabActivo === 'vendedor'">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vendedor
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Pedidos
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Productos
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Promedio Días
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      % Participación
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(vendedor, index) in datosAgrupados.porVendedor" :key="index" class="hover:bg-gray-50">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <div class="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span class="text-blue-600 font-semibold">{{ vendedor.nombre.charAt(0) }}</span>
                        </div>
                        <div class="ml-4">
                          <div class="text-sm font-medium text-gray-900">{{ vendedor.nombre }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {{ vendedor.totalPedidos }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {{ vendedor.totalProductos }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="[
                        'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
                        vendedor.promedioDias <= 3 ? 'bg-green-100 text-green-800' :
                        vendedor.promedioDias <= 7 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      ]">
                        {{ vendedor.promedioDias }} días
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div class="flex items-center">
                        <div class="w-full bg-gray-200 rounded-full h-2 mr-2">
                          <div class="bg-blue-600 h-2 rounded-full" :style="`width: ${vendedor.porcentaje}%`"></div>
                        </div>
                        <span class="text-xs">{{ vendedor.porcentaje }}%</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Tab: Por Producto -->
          <div v-if="tabActivo === 'producto'">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Producto
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cantidad
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Promedio Días
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      % del Total
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(producto, index) in datosAgrupados.porProducto" :key="index" class="hover:bg-gray-50">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm font-medium text-gray-900">{{ producto.nombre }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {{ producto.cantidad }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="[
                        'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
                        producto.promedioDias <= 3 ? 'bg-green-100 text-green-800' :
                        producto.promedioDias <= 7 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      ]">
                        {{ producto.promedioDias }} días
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {{ producto.porcentaje }}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Tab: Por Estatus -->
          <div v-if="tabActivo === 'estatus'">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div v-for="(estatus, index) in datosAgrupados.porEstatus" :key="index" 
                   class="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <div class="flex items-center justify-between mb-4">
                  <h3 class="text-lg font-semibold text-gray-800">{{ estatus.nombre }}</h3>
                  <span :class="[
                    'px-3 py-1 rounded-full text-sm font-semibold',
                    estatus.nombre === 'PROCESO' ? 'bg-yellow-100 text-yellow-800' :
                    estatus.nombre === 'TERMINADO' ? 'bg-green-100 text-green-800' :
                    estatus.nombre === 'ENTREGADO' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  ]">
                    {{ estatus.cantidad }}
                  </span>
                </div>
                <div class="space-y-2">
                  <div class="flex justify-between text-sm">
                    <span class="text-gray-600">Porcentaje:</span>
                    <span class="font-semibold">{{ estatus.porcentaje }}%</span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-2">
                    <div :class="[
                      'h-2 rounded-full',
                      estatus.nombre === 'PROCESO' ? 'bg-yellow-500' :
                      estatus.nombre === 'TERMINADO' ? 'bg-green-500' :
                      estatus.nombre === 'ENTREGADO' ? 'bg-blue-500' :
                      'bg-gray-500'
                    ]" :style="`width: ${estatus.porcentaje}%`"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tab: Detalle Completo -->
          <div v-if="tabActivo === 'detalle'">
            <div class="mb-4">
              <input
                type="text"
                v-model="busqueda"
                placeholder="🔍 Buscar por pedido, cliente, vendedor o producto..."
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pedido
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vendedor
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Producto
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha Venta
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Días Prod.
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estatus
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(item, index) in datosFiltrados" :key="index" class="hover:bg-gray-50">
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{{ item.pedido }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm text-gray-900">{{ item.cliente }}</div>
                      <div class="text-xs text-gray-500">{{ item.phone }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {{ item.vendedor }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {{ item.producto }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ formatearFecha(item.fecha_venta) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="[
                        'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
                        item.dias_produccion <= 3 ? 'bg-green-100 text-green-800' :
                        item.dias_produccion <= 7 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      ]">
                        {{ item.dias_produccion }} días
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="[
                        'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
                        item.estatus === 'PROCESO' ? 'bg-yellow-100 text-yellow-800' :
                        item.estatus === 'TERMINADO' ? 'bg-green-100 text-green-800' :
                        item.estatus === 'ENTREGADO' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      ]">
                        {{ item.estatus }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="mt-4 text-sm text-gray-600">
              Mostrando {{ datosFiltrados.length }} de {{ datosOriginales.length }} registros
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="bg-white rounded-lg shadow-md p-12 text-center">
      <svg class="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <h3 class="text-xl font-semibold text-gray-700 mb-2">No hay datos disponibles</h3>
      <p class="text-gray-500">Selecciona un rango de fechas y haz clic en "Consultar"</p>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import initmixin from '../../../../Services/initmixin';

export default {
  name: 'ReporteProduccionContador',
  data() {
    return {
      cargando: false,
      error: null,
      datosOriginales: [],
      busqueda: '',
      tabActivo: 'vendedor',
      filtros: {
        fechaInicio: this.obtenerFechaInicioPorDefecto(),
        fechaFin: this.obtenerFechaFinPorDefecto()
      },
      tabs: [
        { id: 'vendedor', nombre: '👤 Por Vendedor' },
        { id: 'producto', nombre: '📦 Por Producto' },
        { id: 'estatus', nombre: '📊 Por Estatus' },
        { id: 'detalle', nombre: '📋 Detalle Completo' }
      ]
    };
  },
  computed: {
    resumenGeneral() {
      if (this.datosOriginales.length === 0) {
        return {
          totalPedidos: 0,
          totalProductos: 0,
          totalVendedores: 0,
          promedioDiasProduccion: 0
        };
      }

      const pedidosUnicos = [...new Set(this.datosOriginales.map(item => item.pedido))];
      const vendedoresUnicos = [...new Set(this.datosOriginales.map(item => item.vendedor))];
      const sumaDias = this.datosOriginales.reduce((sum, item) => sum + (item.dias_produccion || 0), 0);

      return {
        totalPedidos: pedidosUnicos.length,
        totalProductos: this.datosOriginales.length,
        totalVendedores: vendedoresUnicos.length,
        promedioDiasProduccion: (sumaDias / this.datosOriginales.length).toFixed(1)
      };
    },
    datosAgrupados() {
      // Agrupar por vendedor
      const porVendedor = {};
      const porProducto = {};
      const porEstatus = {};

      this.datosOriginales.forEach(item => {
        // Por vendedor
        if (!porVendedor[item.vendedor]) {
          porVendedor[item.vendedor] = {
            nombre: item.vendedor,
            totalPedidos: 0,
            totalProductos: 0,
            pedidosUnicos: new Set(),
            sumaDias: 0
          };
        }
        porVendedor[item.vendedor].pedidosUnicos.add(item.pedido);
        porVendedor[item.vendedor].totalProductos++;
        porVendedor[item.vendedor].sumaDias += item.dias_produccion || 0;

        // Por producto
        if (!porProducto[item.producto]) {
          porProducto[item.producto] = {
            nombre: item.producto,
            cantidad: 0,
            sumaDias: 0
          };
        }
        porProducto[item.producto].cantidad++;
        porProducto[item.producto].sumaDias += item.dias_produccion || 0;

        // Por estatus
        if (!porEstatus[item.estatus]) {
          porEstatus[item.estatus] = {
            nombre: item.estatus,
            cantidad: 0
          };
        }
        porEstatus[item.estatus].cantidad++;
      });

      // Convertir a arrays y calcular promedios
      const vendedoresArray = Object.values(porVendedor).map(v => ({
        nombre: v.nombre,
        totalPedidos: v.pedidosUnicos.size,
        totalProductos: v.totalProductos,
        promedioDias: (v.sumaDias / v.totalProductos).toFixed(1),
        porcentaje: ((v.totalProductos / this.datosOriginales.length) * 100).toFixed(1)
      })).sort((a, b) => b.totalProductos - a.totalProductos);

      const productosArray = Object.values(porProducto).map(p => ({
        nombre: p.nombre,
        cantidad: p.cantidad,
        promedioDias: (p.sumaDias / p.cantidad).toFixed(1),
        porcentaje: ((p.cantidad / this.datosOriginales.length) * 100).toFixed(1)
      })).sort((a, b) => b.cantidad - a.cantidad);

      const estatusArray = Object.values(porEstatus).map(e => ({
        nombre: e.nombre,
        cantidad: e.cantidad,
        porcentaje: ((e.cantidad / this.datosOriginales.length) * 100).toFixed(1)
      })).sort((a, b) => b.cantidad - a.cantidad);

      return {
        porVendedor: vendedoresArray,
        porProducto: productosArray,
        porEstatus: estatusArray
      };
    },
    datosFiltrados() {
      if (!this.busqueda) return this.datosOriginales;

      const busquedaLower = this.busqueda.toLowerCase();
      return this.datosOriginales.filter(item => 
        item.pedido.toString().includes(busquedaLower) ||
        item.cliente.toLowerCase().includes(busquedaLower) ||
        item.vendedor.toLowerCase().includes(busquedaLower) ||
        item.producto.toLowerCase().includes(busquedaLower)
      );
    }
  },
  methods: {
    obtenerFechaInicioPorDefecto() {
      const fecha = new Date();
      fecha.setDate(1); // Primer día del mes
      return fecha.toISOString().split('T')[0];
    },
    obtenerFechaFinPorDefecto() {
      const fecha = new Date();
      return fecha.toISOString().split('T')[0];
    },
    async cargarDatos() {
      this.cargando = true;
      this.error = null;

      try {
        const fechaInicioISO = new Date(this.filtros.fechaInicio).toISOString();
        const fechaFinISO = new Date(this.filtros.fechaFin).toISOString();

        const url = `reportes/produccion/contador?fechaInicio=${fechaInicioISO}&fechaFin=${fechaFinISO}`;
        const response = await initmixin.formatrespuesta('get', null, url, true);

        if (response.error) {
          this.error = response.error;
          return;
        }

        this.datosOriginales = response || [];
        
        if (this.datosOriginales.length === 0) {
          this.error = 'No se encontraron datos para el período seleccionado';
        }
      } catch (err) {
        console.error('Error al cargar datos:', err);
        this.error = err.message || 'Error al cargar los datos del reporte';
      } finally {
        this.cargando = false;
      }
    },
    formatearFecha(fecha) {
      if (!fecha) return 'N/A';
      const date = new Date(fecha);
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },
    exportarExcel() {
      if (this.datosOriginales.length === 0) {
        alert('No hay datos para exportar');
        return;
      }

      // Crear libro de trabajo
      const wb = XLSX.utils.book_new();

      // Hoja 1: Resumen General
      const resumenData = [
        ['Reporte de Producción'],
        ['Período:', `${this.filtros.fechaInicio} - ${this.filtros.fechaFin}`],
        [],
        ['Total Pedidos:', this.resumenGeneral.totalPedidos],
        ['Total Productos:', this.resumenGeneral.totalProductos],
        ['Total Vendedores:', this.resumenGeneral.totalVendedores],
        ['Promedio Días Producción:', this.resumenGeneral.promedioDiasProduccion]
      ];
      const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
      XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

      // Hoja 2: Por Vendedor
      const wsVendedor = XLSX.utils.json_to_sheet(this.datosAgrupados.porVendedor);
      XLSX.utils.book_append_sheet(wb, wsVendedor, 'Por Vendedor');

      // Hoja 3: Por Producto
      const wsProducto = XLSX.utils.json_to_sheet(this.datosAgrupados.porProducto);
      XLSX.utils.book_append_sheet(wb, wsProducto, 'Por Producto');

      // Hoja 4: Por Estatus
      const wsEstatus = XLSX.utils.json_to_sheet(this.datosAgrupados.porEstatus);
      XLSX.utils.book_append_sheet(wb, wsEstatus, 'Por Estatus');

      // Hoja 5: Detalle Completo
      const detalleFormateado = this.datosOriginales.map(item => ({
        'Pedido': item.pedido,
        'Cliente': item.cliente,
        'Teléfono': item.phone,
        'Vendedor': item.vendedor,
        'Producto': item.producto,
        'Fecha Pedido': item.fecha_pedido,
        'Fecha Venta': item.fecha_venta,
        'Fecha Producción': item.fecha_produccion || 'N/A',
        'Fecha Terminado': item.fecha_terminado || 'N/A',
        'Días Producción': item.dias_produccion,
        'Instalación': item.instalacion,
        'Estatus': item.estatus,
        'Cantidad': item.cantidad
      }));
      const wsDetalle = XLSX.utils.json_to_sheet(detalleFormateado);
      XLSX.utils.book_append_sheet(wb, wsDetalle, 'Detalle Completo');

      // Guardar archivo
      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `Reporte_Produccion_${fecha}.xlsx`);
    }
  },
  mounted() {
    // Cargar datos automáticamente al montar el componente
    this.cargarDatos();
  }
};
</script>

<style scoped>
.reporte-produccion-contador {
  padding: 1.5rem;
  min-height: 100vh;
  background-color: #f3f4f6;
}

/* Animaciones */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.reporte-produccion-contador > div {
  animation: fadeIn 0.3s ease-in-out;
}

/* Scrollbar personalizado */
.overflow-x-auto::-webkit-scrollbar {
  height: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
